import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  ArrowLeft, Handshake, UsersRound, Users, Plus, Loader2,
  MoreVertical, Trash2, Edit, UserPlus, Copy, Check, Link2, Shield, Save, Eye, Inbox,
} from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const APP_ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';

const ROLE_OPTIONS = [
  { value: 'comercial', label: 'Comercial' },
  { value: 'chefe_equipa', label: 'Chefe de Equipa' },
  { value: 'backoffice', label: 'Backoffice' },
  { value: 'admin', label: 'Admin' },
  { value: 'parceiro', label: 'Parceiro' },
];

const DEFAULT_PERMISSIONS = {
  // Acções (defense-in-depth backend)
  ver_contratos: true,
  ver_contratos_detalhe: true,
  criar_contratos: true,
  editar_contrato: true,
  editar_estado_inline: false,
  ver_leads: true,
  criar_leads: true,
  editar_leads: true,
  ver_prod_parceiros: true,
  ver_prod_comerciais: true,
  ver_prod_angariadores: true,
  ver_oportunidades: true,
  ver_financeiro: false,
  gerir_membros: true,
  gerir_equipas: true,
  exportar_excel: true,
  // Excel column restriction
  excel_export_restrito: false,
  excel_export_columns: [],
  // Shared Inbox (Claim/Decline): which partner_ids can work the leads of THIS partner
  lead_workers_partner_ids: [],
  // Sidebar visibility (apply to ALL users of this partner)
  sidebar_contratos_geral: true,
  sidebar_crm: true,
  sidebar_conversoes: false,
  sidebar_producao: true,
  sidebar_leads: true,
  sidebar_leads_pipeline: true,
  sidebar_leads_importar: true,
  sidebar_leads_performance: true,
  sidebar_leads_followups: true,
  sidebar_leads_externas: true,
  sidebar_oportunidades: false,
  sidebar_financeiro: true,
  sidebar_financeiro_gestao: true,
  sidebar_financeiro_autos: true,
  sidebar_financeiro_importar: true,
  sidebar_suporte: true,
  sidebar_suporte_tickets: true,
  sidebar_suporte_kb: true,
  sidebar_ferramentas: true,
  sidebar_ferramentas_emails: true,
  sidebar_ferramentas_reunioes: true,
  sidebar_ferramentas_chamadas: true,
  sidebar_ferramentas_calendario: true,
  sidebar_ferramentas_iberdrola: true,
  sidebar_ferramentas_simulador: true,
  sidebar_organizacao: true,
}; 

const PERM_LABELS = {
  ver_contratos: 'Ver Contratos (lista)',
  ver_contratos_detalhe: 'Ver Detalhe de Contratos',
  criar_contratos: 'Criar Contratos',
  editar_contrato: 'Editar Contratos (campos)',
  editar_estado_inline: 'Alterar Estado de Contratos',
  ver_leads: 'Ver Leads',
  criar_leads: 'Criar Leads',
  editar_leads: 'Editar/Alterar Leads',
  ver_prod_parceiros: 'Ver Prod. Parceiros',
  ver_prod_comerciais: 'Ver Prod. Comerciais',
  ver_prod_angariadores: 'Ver Prod. Angariadores',
  ver_oportunidades: 'Ver Oportunidades de Troca',
  ver_financeiro: 'Ver Gestao Financeira',
  gerir_membros: 'Gerir Membros',
  gerir_equipas: 'Gerir Equipas',
  exportar_excel: 'Exportar Excel',
};

// Available columns for restricted Excel export, grouped for clarity.
const EXCEL_COLUMN_GROUPS = {
  'Cliente': [
    { key: 'codigo', label: 'Código' },
    { key: 'nome_cliente', label: 'Nome Cliente' },
    { key: 'nif', label: 'NIF' },
    { key: 'telefones_str', label: 'Telefones' },
    { key: 'emails_str', label: 'Emails' },
  ],
  'Produto': [
    { key: 'service_category', label: 'Categoria' },
    { key: 'company_name', label: 'Empresa' },
    { key: 'tipo_segmento', label: 'Tipo Segmento' },
    { key: 'tipo_produto', label: 'Tipo Produto' },
    { key: 'campanha', label: 'Campanha' },
    { key: 'cpe', label: 'CPE' },
    { key: 'cui', label: 'CUI' },
  ],
  'Morada': [
    { key: 'morada_fornecimento', label: 'Morada Fornecimento' },
    { key: 'nr_porta_fornecimento', label: 'Nr Porta' },
    { key: 'andar_fornecimento', label: 'Andar' },
    { key: 'codigo_postal_fornecimento', label: 'Cód. Postal' },
    { key: 'localidade', label: 'Localidade' },
  ],
  'Financeiro': [
    { key: 'iban', label: 'IBAN' },
    { key: 'fatura_eletronica', label: 'Fatura Eletrónica' },
    { key: 'debito_direto', label: 'Débito Direto' },
  ],
  'Estado e Datas': [
    { key: 'status', label: 'Estado' },
    { key: 'data_assinatura', label: 'Data Assinatura' },
    { key: 'agendamento', label: 'Agendamento' },
    { key: 'created_at', label: 'Data Criação' },
    { key: 'observacoes', label: 'Observações' },
  ],
  'Atribuição': [
    { key: 'assigned_to_name', label: 'Proprietário' },
    { key: 'angariador_name', label: 'Angariador' },
    { key: 'created_by_name', label: 'Criado por' },
    { key: 'partner_name', label: 'Parceiro' },
    { key: 'codigo_registo_cu', label: 'Cód. Registo C.U.' },
    { key: 'nome_registo_cu', label: 'Nome Registo C.U.' },
  ],
};

const SIDEBAR_LABELS = {
  sidebar_contratos_geral: 'Contratos Geral',
  sidebar_crm: 'CRM (Energias / Telecom / Alarmes)',
  sidebar_conversoes: 'Conversões (vista simplificada — só leitura)',
  sidebar_producao: 'Producao (Dashboards)',
  sidebar_leads: 'Leads',
  sidebar_leads_pipeline: '↳ Minha Pipeline',
  sidebar_leads_importar: '↳ Importar Leads',
  sidebar_leads_performance: '↳ Performance',
  sidebar_leads_followups: '↳ Follow-ups 1ª Fatura',
  sidebar_leads_externas: '↳ Leads Externas',
  sidebar_oportunidades: '↳ Oportunidades de Troca',
  sidebar_financeiro: 'Financeiro',
  sidebar_financeiro_gestao: '↳ Gestão Financeira',
  sidebar_financeiro_autos: '↳ Autos',
  sidebar_financeiro_importar: '↳ Importar Contratos',
  sidebar_suporte: 'Suporte',
  sidebar_suporte_tickets: '↳ Tickets',
  sidebar_suporte_kb: '↳ Base de Conhecimento',
  sidebar_ferramentas: 'Ferramentas (mostrar secção)',
  sidebar_ferramentas_emails: '↳ Emails',
  sidebar_ferramentas_reunioes: '↳ Reuniões',
  sidebar_ferramentas_chamadas: '↳ Chamadas',
  sidebar_ferramentas_calendario: '↳ Calendário',
  sidebar_ferramentas_iberdrola: '↳ Verificar Iberdrola',
  sidebar_ferramentas_simulador: '↳ Simulador Energia',
  sidebar_organizacao: 'Organizacao',
};

export const PartnerManagePage = () => {
  const { partnerId } = useParams();
  const navigate = useNavigate();

  const [partner, setPartner] = useState(null);
  const [teams, setTeams] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allPartnersForPool, setAllPartnersForPool] = useState([]);

  // Team dialog
  const [showTeamDialog, setShowTeamDialog] = useState(false);
  const [teamForm, setTeamForm] = useState({ name: '', description: '' });
  const [savingTeam, setSavingTeam] = useState(false);

  // Edit partner dialog
  const [showEditPartner, setShowEditPartner] = useState(false);
  const [partnerForm, setPartnerForm] = useState({ name: '', commission_code: '', description: '', parent_partner_id: '' });

  // Permissions
  const [perms, setPerms] = useState(DEFAULT_PERMISSIONS);
  const [savingPerms, setSavingPerms] = useState(false);
  const [savingPartner, setSavingPartner] = useState(false);

  // User permissions editor
  const [editUserPerms, setEditUserPerms] = useState(null); // { id, name, permissions }
  const [userPerms, setUserPerms] = useState({});
  const [savingUserPerms, setSavingUserPerms] = useState(false);

  // Invite config
  const [inviteConfig, setInviteConfig] = useState(null);
  const [inviteForm, setInviteForm] = useState({ role: 'comercial', team_id: '', team_leader_id: '' });
  const [savingInvite, setSavingInvite] = useState(false);
  const [copied, setCopied] = useState(false);
  const [chefes, setChefes] = useState([]);

  const fetchData = async () => {
    try {
      const [partnersRes, teamsRes, usersRes, inviteRes] = await Promise.all([
        axios.get(`${API_URL}/partners`),
        axios.get(`${API_URL}/teams`, { params: { partner_id: partnerId } }),
        axios.get(`${API_URL}/users`),
        axios.get(`${API_URL}/partners/${partnerId}/invite-config`),
      ]);

      const p = partnersRes.data.find(x => x.id === partnerId);
      if (!p) {
        toast.error('Parceiro nao encontrado');
        navigate('/partners');
        return;
      }
      setPartner(p);
      setAllPartnersForPool(partnersRes.data || []);
      setTeams(teamsRes.data);
      const partnerMembers = usersRes.data.filter(u => u.partner_id === partnerId);
      setMembers(partnerMembers);
      setChefes(usersRes.data.filter(u => u.role === 'chefe_equipa' && u.partner_id === partnerId));
      setPerms({ ...DEFAULT_PERMISSIONS, ...(p.permissions || {}) });
      setInviteConfig(inviteRes.data);

      if (inviteRes.data.configured) {
        setInviteForm({
          role: inviteRes.data.role || 'comercial',
          team_id: inviteRes.data.team_id || '',
          team_leader_id: inviteRes.data.team_leader_id || '',
        });
      }
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [partnerId]);

  // Team CRUD
  const handleCreateTeam = async () => {
    if (!teamForm.name.trim()) { toast.error('Nome da equipa é obrigatório'); return; }
    setSavingTeam(true);
    try {
      await axios.post(`${API_URL}/teams`, { ...teamForm, partner_id: partnerId });
      toast.success('Equipa criada');
      setShowTeamDialog(false);
      setTeamForm({ name: '', description: '' });
      fetchData();
    } catch (err) {
      toast.error('Erro ao criar equipa');
    } finally {
      setSavingTeam(false);
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Eliminar esta equipa? Os membros serao desvinculados.')) return;
    try {
      await axios.delete(`${API_URL}/teams/${teamId}`);
      toast.success('Equipa eliminada');
      fetchData();
    } catch (err) {
      toast.error('Erro ao eliminar equipa');
    }
  };

  // Edit partner
  const openEditPartner = () => {
    if (!partner) return;
    setPartnerForm({
      name: partner.name,
      commission_code: partner.commission_code || '',
      description: partner.description || '',
      parent_partner_id: partner.parent_partner_id || '',
    });
    setShowEditPartner(true);
  };

  const handleSavePartner = async () => {
    if (!partnerForm.name.trim()) { toast.error('Nome é obrigatório'); return; }
    setSavingPartner(true);
    try {
      await axios.put(`${API_URL}/partners/${partnerId}`, partnerForm);
      toast.success('Parceiro atualizado');
      setShowEditPartner(false);
      fetchData();
    } catch (err) {
      toast.error('Erro ao atualizar parceiro');
    } finally {
      setSavingPartner(false);
    }
  };


  const handleSavePermissions = async () => {
    setSavingPerms(true);
    try {
      await axios.put(`${API_URL}/partners/${partnerId}/permissions`, { permissions: perms });
      toast.success('Permissoes do parceiro atualizadas');
    } catch (err) {
      toast.error('Erro ao guardar permissoes');
    } finally {
      setSavingPerms(false);
    }
  };

  const openUserPerms = (member) => {
    setEditUserPerms({ id: member.id, name: member.name });
    setUserPerms({ ...DEFAULT_PERMISSIONS, ...(member.user_permissions || {}) });
  };

  const handleSaveUserPerms = async () => {
    if (!editUserPerms) return;
    setSavingUserPerms(true);
    try {
      await axios.put(`${API_URL}/users/${editUserPerms.id}/permissions`, { permissions: userPerms });
      toast.success(`Permissoes de ${editUserPerms.name} atualizadas`);
      setEditUserPerms(null);
      fetchData();
    } catch (err) {
      toast.error('Erro ao guardar permissoes');
    } finally {
      setSavingUserPerms(false);
    }
  };

  // Invite config
  const handleSaveInvite = async () => {
    setSavingInvite(true);
    try {
      await axios.put(`${API_URL}/partners/${partnerId}/invite-config`, {
        role: inviteForm.role,
        team_id: inviteForm.team_id || null,
        team_leader_id: inviteForm.team_leader_id || null,
      });
      toast.success('Link de registo atualizado');
      const res = await axios.get(`${API_URL}/partners/${partnerId}/invite-config`);
      setInviteConfig(res.data);
    } catch (err) {
      toast.error('Erro ao guardar configuracao');
    } finally {
      setSavingInvite(false);
    }
  };

  const copyLink = () => {
    if (!inviteConfig?.code) return;
    navigator.clipboard.writeText(`${APP_ORIGIN}/convite/${inviteConfig.code}`);
    setCopied(true);
    toast.success('Link copiado!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  if (!partner) return null;

  return (
    <Layout>
      <div className="space-y-6" data-testid="partner-manage-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/partners')} className="h-9 w-9" data-testid="back-to-partners-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-outfit flex items-center gap-2" data-testid="partner-name-heading">
                <Handshake className="w-6 h-6 text-[#2D3E6F]" />
                {partner.name}
              </h1>
              {partner.commission_code && (
                <span className="inline-flex items-center mt-1 px-2.5 py-0.5 rounded-full text-xs font-mono font-semibold bg-amber-50 text-amber-700">
                  {partner.commission_code}
                </span>
              )}
              {partner.parent_partner_name && (
                <span className="inline-flex items-center mt-1 ml-2 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">
                  Pertence a: {partner.parent_partner_name}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="rounded-full gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={() => window.open(`/?preview_as_partner=${partnerId}`, '_blank')}
              data-testid="preview-as-partner-btn"
            >
              <Eye className="w-4 h-4" /> Pré-visualizar como este parceiro
            </Button>
            <Button variant="outline" className="rounded-full gap-2" onClick={openEditPartner} data-testid="edit-partner-btn">
              <Edit className="w-4 h-4" /> Editar Parceiro
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-blue-50"><Users className="w-5 h-5 text-blue-600" /></div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{members.length}</p>
                <p className="text-sm text-slate-500">Membros</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-teal-50"><UsersRound className="w-5 h-5 text-teal-600" /></div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{teams.length}</p>
                <p className="text-sm text-slate-500">Equipas</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-emerald-50"><Handshake className="w-5 h-5 text-emerald-600" /></div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${partner.active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {partner.active ? 'Ativo' : 'Inativo'}
                  </span>
                </p>
                <p className="text-sm text-slate-500">Estado</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Novo User - Invite Section */}
        <Card className="rounded-2xl border-slate-100 border-l-4 border-l-[#2D3E6F]">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-outfit flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#2D3E6F]" />
              Novo User
            </CardTitle>
            <p className="text-sm text-slate-500">
              Link para enviar ao novo utilizador do CRM {partner.name}
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs text-slate-500 uppercase tracking-wider">Funcao</Label>
                <Select
                  value={inviteForm.role}
                  onValueChange={(v) => setInviteForm(f => ({ ...f, role: v }))}
                >
                  <SelectTrigger className="mt-1.5 h-10" data-testid="invite-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500 uppercase tracking-wider">Equipa</Label>
                <Select
                  value={inviteForm.team_id || 'none'}
                  onValueChange={(v) => setInviteForm(f => ({ ...f, team_id: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger className="mt-1.5 h-10" data-testid="invite-team-select">
                    <SelectValue placeholder="Sem equipa" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem equipa</SelectItem>
                    {teams.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500 uppercase tracking-wider">Chefe de Equipa</Label>
                <Select
                  value={inviteForm.team_leader_id || 'none'}
                  onValueChange={(v) => setInviteForm(f => ({ ...f, team_leader_id: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger className="mt-1.5 h-10" data-testid="invite-leader-select">
                    <SelectValue placeholder="Sem chefe" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem chefe de equipa</SelectItem>
                    {chefes.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                onClick={handleSaveInvite}
                disabled={savingInvite}
                className="bg-[#2D3E6F] hover:bg-[#1a2a4a] rounded-full gap-2"
                data-testid="save-invite-config-btn"
              >
                {savingInvite ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                {inviteConfig?.configured ? 'Atualizar Link' : 'Gerar Link'}
              </Button>
            </div>

            {inviteConfig?.configured && (
              <div className="bg-slate-50 rounded-xl p-4 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-400 mb-1">Link de registo ({inviteConfig.uses || 0} registos)</p>
                  <p className="text-sm font-mono text-[#2D3E6F] truncate" data-testid="invite-link-text">
                    {APP_ORIGIN}/convite/{inviteConfig.code}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  className="shrink-0 h-10 w-10 rounded-full"
                  onClick={copyLink}
                  data-testid="copy-invite-link-btn"
                >
                  {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Equipas Section */}
        <Card className="rounded-2xl border-slate-100">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-outfit flex items-center gap-2">
                <UsersRound className="w-5 h-5 text-teal-500" />
                Equipas
              </CardTitle>
              <Button
                onClick={() => { setTeamForm({ name: '', description: '' }); setShowTeamDialog(true); }}
                className="bg-[#2D3E6F] hover:bg-[#1a2a4a] rounded-full gap-2 h-9"
                data-testid="create-team-btn"
              >
                <Plus className="w-4 h-4" /> Nova Equipa
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {teams.length === 0 ? (
              <div className="text-center py-10">
                <UsersRound className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-400 text-sm">Nenhuma equipa neste parceiro</p>
                <Button variant="outline" className="mt-3" onClick={() => { setTeamForm({ name: '', description: '' }); setShowTeamDialog(true); }}>
                  Criar primeira equipa
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/50">
                      <TableHead className="font-semibold">Nome</TableHead>
                      <TableHead className="font-semibold">Descricao</TableHead>
                      <TableHead className="font-semibold">Membros</TableHead>
                      <TableHead className="w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {teams.map((team) => (
                      <TableRow key={team.id} data-testid={`team-row-${team.id}`}>
                        <TableCell className="font-medium">{team.name}</TableCell>
                        <TableCell className="text-sm text-slate-500">{team.description || '-'}</TableCell>
                        <TableCell>
                          <span className="flex items-center gap-1.5 text-sm">
                            <Users className="w-4 h-4 text-slate-400" /> {team.member_count}
                          </span>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleDeleteTeam(team.id)} className="text-red-600">
                                <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Membros Section */}
        <Card className="rounded-2xl border-slate-100">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-outfit flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              Membros
            </CardTitle>
          </CardHeader>
          <CardContent>
            {members.length === 0 ? (
              <div className="text-center py-10">
                <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-400 text-sm">Nenhum membro associado a este parceiro</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/50">
                      <TableHead className="font-semibold">Nome</TableHead>
                      <TableHead className="font-semibold">Email</TableHead>
                      <TableHead className="font-semibold">Role</TableHead>
                      <TableHead className="font-semibold">Equipa</TableHead>
                      <TableHead className="font-semibold w-28">Permissoes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {members.map((m) => (
                      <TableRow key={m.id} data-testid={`member-row-${m.id}`}>
                        <TableCell className="font-medium">{m.name}</TableCell>
                        <TableCell className="text-sm text-slate-500">{m.email}</TableCell>
                        <TableCell>
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 capitalize">
                            {m.role?.replace('_', ' ')}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">{m.team_name || '-'}</TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1.5 text-xs"
                            onClick={() => openUserPerms(m)}
                            data-testid={`user-perms-btn-${m.id}`}
                          >
                            <Shield className="w-3.5 h-3.5" /> Editar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Permissions Section - only visible to super_admin/gestor or parent admin */}
        <Card className="rounded-2xl border-slate-100">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-outfit flex items-center gap-2">
                <Shield className="w-5 h-5 text-indigo-500" />
                Permissoes do Parceiro
              </CardTitle>
              <Button
                size="sm"
                className="bg-[#2D3E6F] hover:bg-[#1a2a4a] gap-2 rounded-full"
                onClick={handleSavePermissions}
                disabled={savingPerms}
                data-testid="save-permissions-btn"
              >
                {savingPerms ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Guardar
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-1">Defina o que os utilizadores deste parceiro podem ver e fazer</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(PERM_LABELS).map(([key, label]) => (
                <div key={key} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors">
                  <Label className="text-sm cursor-pointer" htmlFor={`perm-${key}`}>{label}</Label>
                  <Switch
                    id={`perm-${key}`}
                    checked={perms[key] ?? DEFAULT_PERMISSIONS[key]}
                    onCheckedChange={(v) => setPerms(prev => ({ ...prev, [key]: v }))}
                    data-testid={`perm-${key}`}
                  />
                </div>
              ))}
            </div>

            {/* Sidebar visibility - applies to ALL users of this partner */}
            <div className="mt-6 pt-5 border-t border-slate-100">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-1">
                <Shield className="w-4 h-4 text-emerald-500" />
                Visibilidade da Barra Lateral
              </h3>
              <p className="text-xs text-slate-400 mb-4">
                Controla o que aparece na sidebar para <strong>todos os utilizadores</strong> deste parceiro
                (admin, comerciais, angariadores, etc). Desligar uma seccao oculta-a por completo.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(SIDEBAR_LABELS).map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:border-emerald-200 transition-colors">
                    <Label className="text-sm cursor-pointer" htmlFor={`perm-${key}`}>{label}</Label>
                    <Switch
                      id={`perm-${key}`}
                      checked={perms[key] ?? DEFAULT_PERMISSIONS[key]}
                      onCheckedChange={(v) => setPerms(prev => ({ ...prev, [key]: v }))}
                      data-testid={`perm-${key}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Shared Inbox (Claim/Decline) — Which other partners can work these leads */}
            <div className="mt-6 pt-5 border-t border-slate-100">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-1">
                <Inbox className="w-4 h-4 text-indigo-500" />
                Shared Inbox — Parceiros que trabalham estas leads
              </h3>
              <p className="text-xs text-slate-400 mb-4">
                Quando este parceiro submete uma lead (link público), ela entra num <strong>pool partilhado</strong> em vez de ser directamente atribuída.
                Escolhe abaixo os <strong>parceiros cujos admins/parceiros</strong> podem aceitar a lead. Super Admins e Gestores podem sempre aceitar.
                O primeiro a aceitar ganha; se todos descartarem, Super Admins recebem alerta forte.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {allPartnersForPool.filter(p => p.id !== partnerId).map(p => {
                  const selected = (perms.lead_workers_partner_ids || []).includes(p.id);
                  return (
                    <label
                      key={p.id}
                      className={`flex items-center gap-2 p-2.5 rounded-lg border text-sm cursor-pointer transition-colors ${
                        selected ? 'border-indigo-300 bg-indigo-50' : 'border-slate-100 hover:border-indigo-200'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={(e) => {
                          const cur = perms.lead_workers_partner_ids || [];
                          const next = e.target.checked ? [...cur, p.id] : cur.filter(k => k !== p.id);
                          setPerms(prev => ({ ...prev, lead_workers_partner_ids: next }));
                        }}
                        className="h-4 w-4 rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500"
                        data-testid={`lead-worker-${p.id}`}
                      />
                      <span className="truncate">{p.name}</span>
                    </label>
                  );
                })}
              </div>
              {(perms.lead_workers_partner_ids || []).length === 0 && (
                <p className="text-xs text-slate-400 mt-3 italic">
                  Nenhum parceiro selecionado — as leads deste parceiro serão atribuídas normalmente ao comercial do link público.
                </p>
              )}
            </div>

            {/* Excel Export — column whitelist */}
            <div className="mt-6 pt-5 border-t border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-amber-500" />
                    Restringir colunas no Excel
                  </h3>
                  <p className="text-xs text-slate-400 mt-1">
                    Quando ativo, o parceiro só pode exportar as colunas marcadas abaixo. Aplica-se mesmo se manipular o URL.
                  </p>
                </div>
                <Switch
                  checked={!!perms.excel_export_restrito}
                  onCheckedChange={(v) => setPerms(prev => ({ ...prev, excel_export_restrito: v }))}
                  data-testid="perm-excel_export_restrito"
                />
              </div>
              {perms.excel_export_restrito && (
                <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {Object.entries(EXCEL_COLUMN_GROUPS).map(([groupName, cols]) => (
                    <div key={groupName} className="rounded-lg border border-amber-100 bg-amber-50/30 p-3">
                      <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-2">{groupName}</p>
                      <div className="space-y-1.5">
                        {cols.map(col => {
                          const checked = (perms.excel_export_columns || []).includes(col.key);
                          return (
                            <label key={col.key} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer hover:text-slate-900">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const cur = perms.excel_export_columns || [];
                                  const next = e.target.checked
                                    ? [...cur, col.key]
                                    : cur.filter(k => k !== col.key);
                                  setPerms(prev => ({ ...prev, excel_export_columns: next }));
                                }}
                                data-testid={`excel-col-${col.key}`}
                                className="h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                              />
                              <span>{col.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create Team Dialog */}
      <Dialog open={showTeamDialog} onOpenChange={setShowTeamDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nova Equipa - {partner.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nome *</Label>
              <Input value={teamForm.name} onChange={(e) => setTeamForm(f => ({ ...f, name: e.target.value }))} className="mt-1 h-11 rounded-lg" placeholder="Nome da equipa" data-testid="team-name-input" />
            </div>
            <div>
              <Label>Descricao</Label>
              <Textarea value={teamForm.description} onChange={(e) => setTeamForm(f => ({ ...f, description: e.target.value }))} className="mt-1" placeholder="Descricao opcional..." data-testid="team-description-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTeamDialog(false)} className="rounded-full">Cancelar</Button>
            <Button onClick={handleCreateTeam} disabled={savingTeam} className="bg-[#2D3E6F] hover:bg-[#1a2a4a] rounded-full gap-2" data-testid="save-team-btn">
              {savingTeam ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Criar Equipa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Partner Dialog */}
      <Dialog open={showEditPartner} onOpenChange={setShowEditPartner}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Parceiro</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nome *</Label>
              <Input value={partnerForm.name} onChange={(e) => setPartnerForm(f => ({ ...f, name: e.target.value }))} className="mt-1 h-11 rounded-lg" data-testid="edit-partner-name-input" />
            </div>
            <div>
              <Label>Codigo Comissao</Label>
              <Input value={partnerForm.commission_code} onChange={(e) => setPartnerForm(f => ({ ...f, commission_code: e.target.value }))} className="mt-1 h-11 rounded-lg" placeholder="Ex: COM01" data-testid="edit-partner-commission-input" />
            </div>
            <div>
              <Label>Descricao</Label>
              <Input value={partnerForm.description} onChange={(e) => setPartnerForm(f => ({ ...f, description: e.target.value }))} className="mt-1 h-11 rounded-lg" data-testid="edit-partner-description-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditPartner(false)} className="rounded-full">Cancelar</Button>
            <Button onClick={handleSavePartner} disabled={savingPartner} className="bg-[#2D3E6F] hover:bg-[#1a2a4a] rounded-full gap-2" data-testid="save-edit-partner-btn">
              {savingPartner ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Atualizar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Permissions Dialog */}
      <Dialog open={!!editUserPerms} onOpenChange={(v) => { if (!v) setEditUserPerms(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="user-permissions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-indigo-500" />
              Permissoes de {editUserPerms?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="py-3 space-y-3 max-h-[60vh] overflow-y-auto">
            <p className="text-xs text-slate-400">Defina o que este utilizador pode ver e fazer. As permissoes individuais substituem as do parceiro.</p>
            {Object.entries(PERM_LABELS).map(([key, label]) => (
              <div key={key} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors">
                <Label className="text-sm cursor-pointer" htmlFor={`uperm-${key}`}>{label}</Label>
                <Switch
                  id={`uperm-${key}`}
                  checked={userPerms[key] ?? DEFAULT_PERMISSIONS[key]}
                  onCheckedChange={(v) => setUserPerms(prev => ({ ...prev, [key]: v }))}
                  data-testid={`uperm-${key}`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUserPerms(null)} className="rounded-full">Cancelar</Button>
            <Button onClick={handleSaveUserPerms} disabled={savingUserPerms} className="bg-[#2D3E6F] hover:bg-[#1a2a4a] rounded-full gap-2" data-testid="save-user-perms-btn">
              {savingUserPerms ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default PartnerManagePage;
